/** Read model for the deployed SMS agent, projected off the raw AIAgentConfiguration record.
 *
 *  A tool definition is an ElevenLabs webhook blob, and it carries credentials: `api_schema` has
 *  `request_headers`, `auth_connection` and `auth_resolved_params`, and on dev one tool holds a live
 *  `Authorization` header. The `tools` field is a JSON scalar, so the whole blob arrives whether we
 *  want it or not. Projecting to these types at the boundary is what keeps a credential from ever
 *  reaching a component: nothing downstream is handed a shape that could render one. */

/** Where a tool parameter is sent. */
export type ToolParamLocation = 'path' | 'query' | 'body'

export type SmsAgentToolParams = Record<ToolParamLocation, string[]>

export type SmsAgentTool = {
  name: string
  /** The description the model reads when deciding whether to call this tool. */
  description: string
  method: string
  url: string
  params: SmsAgentToolParams
  /** True when the tool sends auth of its own. Never the value, only that there is one. */
  authenticated: boolean
}

export type SmsAgentDeployment = {
  prompt: string
  promptVersion?: string
  /** Null when Atlas has never deployed to this location, which is different from an empty prompt. */
  deployedDate?: string
  model?: string
  temperature?: number
  tools: SmsAgentTool[]
}

/** The raw shape as it arrives. `unknown` throughout: this is untrusted vendor JSON, not our model. */
type RawConfig = {
  systemPrompt?: string | null
  promptVersion?: string | null
  promptDeployedDate?: string | null
  model?: string | null
  temperature?: number | null
  tools?: unknown
}

const names = (value: unknown): string[] =>
  value && typeof value === `object` ? Object.keys(value as Record<string, unknown>) : []

function toTool(raw: unknown): SmsAgentTool | undefined {
  if (!raw || typeof raw !== `object`) return undefined
  const tool = raw as Record<string, unknown>
  const name = typeof tool.name === `string` ? tool.name : undefined
  if (!name) return undefined
  const schema = (tool.api_schema ?? {}) as Record<string, unknown>
  const query = (schema.query_params_schema ?? {}) as Record<string, unknown>
  const body = (schema.request_body_schema ?? {}) as Record<string, unknown>
  return {
    name,
    description: typeof tool.description === `string` ? tool.description : ``,
    method: typeof schema.method === `string` ? schema.method : `GET`,
    url: typeof schema.url === `string` ? schema.url : ``,
    params: {
      path: names(schema.path_params_schema),
      query: names(query.properties),
      body: names(body.properties),
    },
    authenticated:
      names(schema.request_headers).length > 0 ||
      !!schema.auth_connection ||
      (Array.isArray(schema.auth_resolved_params) && schema.auth_resolved_params.length > 0),
  }
}

export function toDeployment(config: RawConfig | null | undefined): SmsAgentDeployment | undefined {
  if (!config) return undefined
  const tools = Array.isArray(config.tools) ? config.tools : []
  return {
    prompt: config.systemPrompt ?? ``,
    promptVersion: config.promptVersion ?? undefined,
    deployedDate: config.promptDeployedDate ?? undefined,
    model: config.model ?? undefined,
    temperature: config.temperature ?? undefined,
    tools: tools.map(toTool).filter((tool): tool is SmsAgentTool => !!tool),
  }
}

/** Three states, and they are not the same thing. A location Atlas has never deployed to is not the
 *  same as one deployed with nothing in it, and the agent stands down on both (TRK-5676). */
export function deploymentState(deployment: SmsAgentDeployment | undefined): 'never' | 'empty' | 'deployed' {
  if (!deployment || !deployment.deployedDate) return `never`
  return deployment.prompt.trim() ? `deployed` : `empty`
}
